import React from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Stack,
  MenuItem,
  Container,
  Typography,
  IconButton,
  Box,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useStyles } from '../styles/projectFormModalStyles';
import ModalComponent from '../../../../components/modal-layout/ModalComponent';
import Iconify from '../../../../components/iconify';
import { ADD_PROJECT_FORM_TITLE, EDIT_PROJECT_FORM_TITLE, INITIAL_PROJECT_FORM_STATE } from '../../constants';
import ProjectSchema from '../../schema/projectSchema';
import { PROJECT_STATUS_LIST } from '../../../../Constants/ProjectConstants';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const ProjectFormModal = ({ open, action, onClose, onSubmit, clientList }) => {
  const classes = useStyles();

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: INITIAL_PROJECT_FORM_STATE,
    validationSchema: ProjectSchema,
    onSubmit: handleSubmit,
  });

  const { values, touched, errors, getFieldProps, setFieldValue, submitForm, isSubmitting, resetForm } = formik;

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_PROJECT_FORM_STATE });
  };

  let headerText = '';
  if (action === 'create') {
    headerText = ADD_PROJECT_FORM_TITLE;
  } else if (action === 'update') {
    headerText = EDIT_PROJECT_FORM_TITLE;
  }

  return (
    <ModalComponent openModal={open} handleClose={handleClose}>
      <Box className={`${classes.container} ${classes.inputBoxStyle}`}>
        <Typography variant="h5" align="center" className={classes.memberUpdateModelTitle}>
          {headerText}
        </Typography>
        <IconButton color="primary" onClick={handleClose}>
          <Iconify icon="ic:round-clear" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <Container maxWidth="sm">
        <FormikProvider value={formik}>
          <Form onSubmit={formik?.handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Project Name"
                {...getFieldProps('projectName')}
                error={touched.projectName && Boolean(errors.projectName)}
                helperText={touched.projectName && errors.projectName}
                fullWidth
              />
              <FormControl fullWidth error={touched.clientId && Boolean(errors.clientId)}>
                <InputLabel>Client</InputLabel>
                <Select
                  {...getFieldProps('clientId')}
                  helperText={touched.clientId && errors.clientId}
                  label="Client"
                  onChange={(event, value) => {
                    setFieldValue('clientId', value?.props?.value);
                  }}
                >
                  {clientList.map((client, i) => (
                    <MenuItem value={client.id} key={i}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.clientId && errors.clientId && (
                  <FormHelperText className={classes.helperText}>{errors.clientId}</FormHelperText>
                )}
              </FormControl>
              <Stack direction="row" spacing={2}>
                <TextField
                  {...getFieldProps('initialEstimatedHrs')}
                  name="initialEstimatedHrs"
                  label="Project Estimated Hours"
                  type="text"
                  inputMode="numeric"
                  value={values.initialEstimatedHrs}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);
                    if (isValidInput && inputValue >= 0) {
                      setFieldValue('initialEstimatedHrs', inputValue);
                    }
                  }}
                  error={touched.initialEstimatedHrs && Boolean(errors.initialEstimatedHrs)}
                  helperText={touched.initialEstimatedHrs && errors.initialEstimatedHrs}
                  fullWidth
                />
                <TextField
                  {...getFieldProps('totalHrs')}
                  name="totalHrs"
                  label="Project Budget Hours"
                  type="text"
                  inputMode="numeric"
                  value={values.totalHrs}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);
                    if (isValidInput && inputValue >= 0) {
                      setFieldValue('totalHrs', inputValue);
                    }
                  }}
                  error={touched.totalHrs && Boolean(errors.totalHrs)}
                  helperText={touched.totalHrs && errors.totalHrs}
                  fullWidth
                />
              </Stack>
              {action === 'update' && (
                <FormControl fullWidth error={touched.status && Boolean(errors.status)}>
                  <InputLabel>Project Status</InputLabel>
                  <Select
                    {...getFieldProps('status')}
                    label="Project Status"
                    onChange={(event, value) => {
                      setFieldValue('status', value?.props?.value);
                    }}
                  >
                    {PROJECT_STATUS_LIST.map((list, i) => (
                      <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={i}>
                        {list}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.status && errors.status && (
                    <FormHelperText className={classes.helperText}>{errors.status}</FormHelperText>
                  )}
                </FormControl>
              )}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={values.startDate ? dayjs(values.startDate) : null}
                  onChange={(newValue) => {
                    setFieldValue('startDate', newValue);
                  }}
                  inputFormat="DD-MMM-YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched.startDate && Boolean(errors.startDate)}
                      helperText={touched.startDate && errors.startDate}
                      fullWidth
                    />
                  )}
                />
                <DatePicker
                  label="End Date"
                  value={values.endDate ? dayjs(values.endDate) : null}
                  onChange={(newValue) => {
                    setFieldValue('endDate', newValue);
                  }}
                  inputFormat="DD-MMM-YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched.endDate && Boolean(errors.endDate)}
                      helperText={touched.endDate && errors.endDate}
                      fullWidth
                    />
                  )}
                />
              </LocalizationProvider>

              <Box className={classes.checkboxContainer}>
                <Box className={classes.checkboxRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values?.allowOverSpent}
                        {...label}
                        onChange={(event, check) => {
                          setFieldValue('allowOverSpent', check);
                        }}
                      />
                    }
                    label="Allow Excess Hours"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values?.allowManualTask}
                        {...label}
                        onChange={(event, check) => {
                          setFieldValue('allowManualTask', check);
                        }}
                      />
                    }
                    label="Allow Manual Task Entry"
                  />
                </Box>
                <FormControlLabel
                  control={<Checkbox name="includeMe" checked={values.includeMe} {...getFieldProps('includeMe')} />}
                  label="Would you like be the part of this project"
                />
              </Box>

              <LoadingButton fullWidth size="large" variant="contained" onClick={submitForm} loading={isSubmitting}>
                Save
              </LoadingButton>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </ModalComponent>
  );
};

ProjectFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  action: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  clientList: PropTypes.array.isRequired,
};

export default ProjectFormModal;
